/**
 * UserAvatar — system-wide avatar component.
 *
 * Uses Apple SF Symbol paths (person.fill / person.3.fill) as fallbacks
 * instead of text initials so no image = a clean silhouette, not letters.
 *
 * Usage:
 *   <UserAvatar src={user.avatar} name={user.name} size={32} />
 *   <UserAvatar src={null} size={40} variant="group" fallbackBg="var(--primary)" fallbackColor="var(--primary-foreground)" />
 */

import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import { cn } from './utils';
import { initials, avatarColor } from './avatar-color';

// ── SF Symbol paths ───────────────────────────────────────────────────────────

export function PersonFillIcon({
  size = 18,
  color = 'currentColor',
  className,
}: {
  size?: number;
  color?: string;
  className?: string;
}) {
  // viewBox: 0 0 16.7969 17.5684  (aspect ratio ~0.956)
  const height = size * (17.5684 / 16.7969);
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 16.7969 17.5684"
      fill={color}
      aria-hidden="true"
      className={className}
    >
      <path d="M1.66992 17.5586L14.7656 17.5586C15.8105 17.5586 16.4355 17.0703 16.4355 16.2598C16.4355 13.7402 13.2812 10.2637 8.21289 10.2637C3.1543 10.2637 0 13.7402 0 16.2598C0 17.0703 0.625 17.5586 1.66992 17.5586ZM8.22266 8.51562C10.3125 8.51562 12.1289 6.64062 12.1289 4.19922C12.1289 1.78711 10.3125 0 8.22266 0C6.13281 0 4.31641 1.82617 4.31641 4.21875C4.31641 6.64062 6.12305 8.51562 8.22266 8.51562Z" />
    </svg>
  );
}

export function PersonGroupFillIcon({
  size = 20,
  color = 'currentColor',
  className,
}: {
  size?: number;
  color?: string;
  className?: string;
}) {
  // viewBox: 0 0 37.168 20.2832  (aspect ratio ~1.833 wide)
  const height = size * (20.2832 / 37.168);
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 37.168 20.2832"
      fill={color}
      aria-hidden="true"
      className={className}
    >
      <path d="M11.3956 12.8283C9.77236 14.2383 8.85742 16.0397 8.85742 17.6562C8.85742 18.1311 8.97442 18.5742 9.19854 18.9551L1.71875 18.9551C0.478516 18.9551 0 18.4668 0 17.5879C0 14.8828 2.75391 11.6309 7.1582 11.6309C8.82867 11.6309 10.2617 12.0987 11.3956 12.8283ZM10.5566 6.37695C10.5566 8.49609 8.98438 10.1367 7.1582 10.1367C5.3418 10.1367 3.75977 8.49609 3.75977 6.39648C3.75977 4.31641 5.35156 2.72461 7.1582 2.72461C8.97461 2.72461 10.5566 4.27734 10.5566 6.37695Z" />
      <path d="M36.8066 17.5879C36.8066 18.4668 36.3281 18.9551 35.0879 18.9551L27.6199 18.9551C27.843 18.5742 27.959 18.1311 27.959 17.6562C27.959 16.0385 27.0451 14.2356 25.421 12.8252C26.554 12.0974 27.9863 11.6309 29.6582 11.6309C34.0625 11.6309 36.8066 14.8828 36.8066 17.5879ZM33.0566 6.37695C33.0566 8.49609 31.4844 10.1367 29.6582 10.1367C27.8418 10.1367 26.25 8.49609 26.25 6.39648C26.25 4.31641 27.8418 2.72461 29.6582 2.72461C31.4746 2.72461 33.0566 4.27734 33.0566 6.37695Z" />
      <path d="M18.418 9.91211C20.5078 9.91211 22.3242 8.03711 22.3242 5.5957C22.3242 3.18359 20.5078 1.39648 18.418 1.39648C16.3281 1.39648 14.5117 3.22266 14.5117 5.61523C14.5117 8.03711 16.3184 9.91211 18.418 9.91211ZM11.8555 18.9551L24.9609 18.9551C26.0059 18.9551 26.6309 18.4668 26.6309 17.6562C26.6309 15.1367 23.4766 11.6602 18.4082 11.6602C13.3496 11.6602 10.1953 15.1367 10.1953 17.6562C10.1953 18.4668 10.8203 18.9551 11.8555 18.9551Z" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export type UserAvatarVariant = 'person' | 'group';

export interface UserAvatarProps {
  /** Image URL (supports drive-file: protocol via AvatarImage) */
  src?: string | null;
  /** Alt text / display name — used for accessibility only */
  name?: string;
  /** Diameter in px. Default 32. */
  size?: number;
  /** 'person' uses person.fill; 'group' uses person.3.fill. Default 'person'. */
  variant?: UserAvatarVariant;
  /** Background colour for the fallback circle. Default: var(--muted). */
  fallbackBg?: string;
  /** Icon colour for the fallback. Default: var(--muted-foreground). */
  fallbackColor?: string;
  /** Border CSS shorthand, e.g. '1px solid var(--border)'. Default: none. */
  border?: string;
  /** Border radius override. Default: '50%' (full circle). */
  borderRadius?: string | number;
  /**
   * What to show when there is no src image.
   * 'icon'    — SF Symbol silhouette (default, existing behaviour).
   * 'initials'— coloured monogram derived from name.
   */
  fallback?: 'icon' | 'initials';
  className?: string;
  style?: React.CSSProperties;
}

export function UserAvatar({
  src,
  name,
  size = 32,
  variant = 'person',
  fallbackBg = 'var(--muted)',
  fallbackColor = 'var(--muted-foreground)',
  border,
  borderRadius = '50%',
  fallback = 'icon',
  className,
  style,
}: UserAvatarProps) {
  // Icon occupies ~50% of diameter for person, ~58% for the wider group icon
  const iconSize = variant === 'group'
    ? Math.round(size * 0.58)
    : Math.round(size * 0.50);

  const br = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;

  // Resolve fallback colours: explicit overrides win; initials mode uses palette.
  // (sentinel comparison against the default literals lets a caller override either
  //  colour without us mistaking the default for an intentional value)
  const useInitials = fallback === 'initials';
  const color = useInitials ? avatarColor(name) : null;
  const resolvedBg    = fallbackBg    !== 'var(--muted)'            ? fallbackBg    : color ? color.bg : 'var(--muted)';
  const resolvedColor = fallbackColor !== 'var(--muted-foreground)' ? fallbackColor : color ? color.fg : 'var(--muted-foreground)';

  return (
    <Avatar
      // Key by identity so the Radix root remounts when the contact changes.
      // Without this, a reused avatar keeps a stale 'loaded' image status and the
      // fallback (icon/initials) stops rendering after switching from a contact
      // with a photo to one without.
      key={src ?? `i:${name ?? ''}`}
      className={cn('shrink-0', className)}
      style={{
        width: size,
        height: size,
        borderRadius: br,
        border,
        flexShrink: 0,
        ...style,
      }}
    >
      {src && (
        <AvatarImage
          src={src}
          alt={name}
          className="object-cover"
          style={{ borderRadius: br }}
        />
      )}
      <AvatarFallback
        className="flex items-center justify-center"
        style={{ borderRadius: br, background: resolvedBg, color: resolvedColor }}
      >
        {useInitials ? (
          <span
            className="font-apple select-none"
            style={{ fontSize: Math.round(size * 0.4), fontWeight: 600, lineHeight: 1 }}
          >
            {initials(name)}
          </span>
        ) : variant === 'group' ? (
          <PersonGroupFillIcon size={iconSize} color="currentColor" />
        ) : (
          <PersonFillIcon size={iconSize} color="currentColor" />
        )}
      </AvatarFallback>
    </Avatar>
  );
}
